import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { ServicePlanItem } from 'src/features/servicePlanItems/utils/types';
import { useDeleteServicePlanItem } from 'src/features/servicePlanItems/api/useDeleteServicePlanItem';
import { LIST_SERVICE_PLAN_ITEMS_RESOURCE } from 'src/features/servicePlanItems/api/useListServicePlanItems';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { useOnError } from 'src/utils/errors';

interface DeleteServicePlanItemFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanItem: ServicePlanItem;
    children?: React.ReactNode;
}

function DeleteServicePlanItemFormModal<TData>({
    selectedServicePlanItem,
    children,
}: DeleteServicePlanItemFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const { mutate: deleteServicePlanItem } = useDeleteServicePlanItem({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_ITEMS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(servicePlanItemsMessages.deleteServicePlanItemSuccess),
                description: formatMessage(servicePlanItemsMessages.deleteServicePlanItemSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...servicePlanItemsMessages.deleteServicePlanItem },
                subTitle: {
                    ...servicePlanItemsMessages.deleteServicePlanItemSubtitle,
                    values: {
                        entity: selectedServicePlanItem.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...servicePlanItemsMessages.confirmDeleteServicePlanItem },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: servicePlanItemsMessages.deleteServicePlanItem,
                callbackFn: () => {
                    return (
                        deleteServicePlanItem({
                            id: selectedServicePlanItem.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteServicePlanItemFormModal;
